import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import useProduction from '@/src/Production/components/hooks/useProduction';
import { fullName } from './shared';

/* The activation's ops sections (schedule, venue, crew, booth, gear,
   contacts, design, comms, timeline) — rendered inline on the event
   Overview ("that's an activation" — Josh). Sections edit inline; rows
   mutate immediately. Activations born in photostat without job data get
   a single enable affordance. */

const EVENT_TYPES = [
    'brand_activation', 'conference', 'party', 'wedding', 'gala',
    'holiday_party', 'tour', 'photoshoot', 'pop_up', 'launch', 'headshots',
    'other',
];
const STATUSES = [
    'pending', 'ready_for_production', 'in_production', 'show_ready',
    'past', 'wrapped', 'lost',
];
const BOOTH_TYPES = [
    'linus', 'retrofit', 'tether_photographer', 'romr_pro', 'romr_ios',
    'event_photography', 'queso', 'other',
];
const SOFTWARE = [
    'romr', 'clickerbooth_capture', 'clickerbooth_sharing', 'linus',
    'google_pixel_romr', 'photostat_mac', 'snappic',
];
const BACKDROPS = [
    'custom_printed', 'client_provided', 'black_pillowcase',
    'white_pillowcase', 'grey_pillowcase', 'grey', 'na', 'wall',
    'custom_paper_roll', 'fully_custom_build',
];
const STAFF_ROLES = ['lead', 'photographer', 'ba', 'assistant'];
const CONTACT_ROLES = ['primary', 'billing', 'artwork', 'day_of', 'onsite', 'other'];
const ASSIGNMENT_STATUSES = ['pending', 'confirmed', 'declined', 'cancelled'];
const DESIGN_STATUSES = [
    'needs_artwork', 'up_next', 'waiting_on_files', 'to_do',
    'client_designing', 'doing', 'pending_internal_approval',
    'sent_to_client', 'client_changes', 'ready_for_production', 'archived',
];
const DELIVERABLE_STATUSES = [
    'not_started', 'in_progress', 'ready_for_review', 'approved', 'delivered',
];
const label = (s?: string | null) => (s ? s.replaceAll('_', ' ') : '—');

function Field({ label: l, children, wide }: {
    label: string; children: React.ReactNode; wide?: boolean;
}) {
    return (
        <div className={wide ? 'col-span-2' : ''}>
            <div className="ps-label mb-1">{l}</div>
            <div className="text-[13px] font-semibold">
                {children ?? <span className="font-normal text-faint">—</span>}
            </div>
        </div>
    );
}

function EditField({ label: l, children, wide }: {
    label: string; children: React.ReactNode; wide?: boolean;
}) {
    return (
        <div className={wide ? 'col-span-2' : ''}>
            <label className="ps-label mb-1 block">{l}</label>
            {children}
        </div>
    );
}

function SectionHead({ title, editing, onEdit, onSave, onCancel, saving }: {
    title: string; editing: boolean; saving: boolean;
    onEdit: () => void; onSave: () => void; onCancel: () => void;
}) {
    return (
        <div className="flex items-center gap-3">
            <span className="ps-label">{title}</span>
            {!editing && (
                <button
                    type="button"
                    onClick={onEdit}
                    className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-brand-ink"
                >
                    Edit
                </button>
            )}
            {editing && (
                <>
                    <button
                        type="button"
                        disabled={saving}
                        onClick={onSave}
                        className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1 font-[inherit] text-[11.5px] font-semibold text-white disabled:opacity-50"
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-muted-foreground"
                    >
                        Cancel
                    </button>
                </>
            )}
        </div>
    );
}

const toLocalInput = (value?: string | null, tz?: string | null) =>
    value ? moment(value).tz(tz || 'America/New_York').format('YYYY-MM-DDTHH:mm') : '';
const fromLocalInput = (value: string, tz?: string | null) =>
    value ? moment.tz(value, tz || 'America/New_York').toISOString() : null;
const fmtAt = (value?: string | null, tz?: string | null) =>
    value
        ? moment(value).tz(tz || moment.tz.guess()).format('ddd MMM D · h:mm A')
        : null;

export default function ActivationOpsSections({
    activationId: id,
}: {
    activationId: number;
}) {
    const navigate = useNavigate();
    const api = useProduction();

    const [record, setRecord] = useState<any | null>(null);
    const [missing, setMissing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [staffList, setStaffList] = useState<any[]>([]);
    const [venueList, setVenueList] = useState<any[]>([]);
    const [gearList, setGearList] = useState<any[]>([]);
    const [contactList, setContactList] = useState<any[]>([]);

    const [editing, setEditing] = useState<string | null>(null);
    const [draft, setDraft] = useState<any>({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [crewAdd, setCrewAdd] = useState({ staffId: '', role: 'photographer', rate: '', rateType: 'hourly' });
    const [gearAdd, setGearAdd] = useState({ gearAssetId: '' });
    const [contactAdd, setContactAdd] = useState({ contactId: '', role: 'primary' });
    const [deliverableAdd, setDeliverableAdd] = useState<{ [requestId: string]: string }>({});

    const reload = async () => {
        const data = await api.getProductionRecord(id).catch(() => null);
        if (data) setRecord(data);
        else setMissing(true);
        setLoading(false);
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const [rec, staff, venues, gear, contacts] = await Promise.all([
                api.getProductionRecord(id).catch(() => null),
                api.getStaff().catch(() => []),
                api.getVenues().catch(() => []),
                api.getGear().catch(() => []),
                api.getContacts().catch(() => []),
            ]);
            if (!mounted) return;
            if (rec) setRecord(rec);
            else setMissing(true);
            setStaffList(staff ?? []);
            setVenueList(venues ?? []);
            setGearList(gear ?? []);
            setContactList(contacts ?? []);
            setLoading(false);
        })();
        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const startEdit = (section: string, fields: Record<string, any>) => {
        setEditing(section);
        setDraft(fields);
        setError(null);
    };

    const saveEdit = async (fields: Record<string, any>) => {
        setSaving(true);
        setError(null);
        const ok = await api.updateProduction(id, fields).then(() => true).catch(() => false);
        setSaving(false);
        if (ok) {
            setEditing(null);
            await reload();
        } else setError('Save failed');
    };

    const mutate = async (fn: () => Promise<any>) => {
        setError(null);
        const ok = await fn().then(() => true).catch(() => false);
        if (!ok) setError('Action failed');
        await reload();
    };

    if (loading)
        return (
            <div className="py-6 text-[13px] text-faint">Loading job data…</div>
        );

    if (missing || !record)
        return (
            <section className="mt-2">
                <div className="max-w-[460px] rounded-2xl border border-dashed border-border p-4 text-[12.5px] text-muted-foreground">
                    No job data on this activation yet — schedule, crew, venue,
                    and booth config live here once added.{' '}
                    <button
                        type="button"
                        onClick={async () => {
                            await api.enableOps(id).catch(() => null);
                            setMissing(false);
                            setLoading(true);
                            await reload();
                        }}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                    >
                        Add job data
                    </button>
                </div>
            </section>
        );

    const tz = record.timezone;
    const contacts = record.contacts ?? [];
    const crew = record.staffAssignments ?? [];
    const gear = record.gearAssignments ?? [];
    const design = record.designRequests ?? [];
    const activity = record.activity ?? [];

    const selectClass = 'ps-input cursor-pointer appearance-none text-[12.5px]';
    const grid = 'mt-2.5 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4';

    return (
        <div className="mt-2">
            {/* job line + status + actions */}
            <div className="mb-8 flex flex-wrap items-start gap-4">
                <div className="min-w-0 pt-1 text-[12.5px] text-muted-foreground">
                    {record.jobId && (
                        <>
                            <button
                                type="button"
                                onClick={() => navigate(`/v2/jobs/${record.jobId}`)}
                                className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[12.5px] font-semibold text-brand-ink"
                            >
                                job: {record.jobName ?? record.jobId}
                            </button>
                        </>
                    )}
                    {record.attioDealId && (
                        <>
                            {' · '}
                            <a
                                className="font-semibold text-brand-ink"
                                href={`https://app.attio.com/smilebooth/deals/record/${record.attioDealId}/overview`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                from Attio ↗
                            </a>
                        </>
                    )}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    <select
                        className={selectClass}
                        value={record.status}
                        onChange={(e) =>
                            mutate(() =>
                                api.updateProductionStatus(id, e.target.value),
                            )
                        }
                    >
                        {STATUSES.map((s) => (
                            <option key={s} value={s}>
                                {label(s)}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        onClick={async () => {
                            const created = await api
                                .duplicateProduction(id)
                                .catch(() => null);
                            if (created?.id)
                                navigate(`/v2/activations/${created.id}`);
                        }}
                        className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-muted-foreground hover:text-foreground"
                    >
                        Duplicate
                    </button>
                    <button
                        type="button"
                        onClick={async () => {
                            // eslint-disable-next-line no-alert
                            if (!window.confirm('Delete this production? Ops data is removed; the activation stays if it has galleries.')) return;
                            const result = await api
                                .deleteProduction(id)
                                .catch(() => null);
                            if (result) navigate('/v2/jobs');
                        }}
                        className="ps-pill cursor-pointer px-3.5 font-[inherit] text-[12px] font-semibold text-attention"
                    >
                        Delete
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-5 text-[12.5px] font-semibold text-attention">
                    {error}
                </div>
            )}

            {/* basics */}
            <section className="mb-9">
                <SectionHead
                    title="Basics"
                    editing={editing === 'basics'}
                    saving={saving}
                    onEdit={() =>
                        startEdit('basics', {
                            name: record.name ?? '',
                            eventName: record.eventName ?? '',
                            eventType: record.eventType ?? 'other',
                            description: record.description ?? '',
                            billingEntity: record.billingEntity ?? '',
                            guestCount: record.guestCount ?? '',
                        })
                    }
                    onSave={() =>
                        saveEdit({
                            name: draft.name,
                            eventName: draft.eventName || null,
                            eventType: draft.eventType,
                            description: draft.description || null,
                            billingEntity: draft.billingEntity || null,
                            guestCount: draft.guestCount === '' ? null : +draft.guestCount,
                        })
                    }
                    onCancel={() => setEditing(null)}
                />
                {editing !== 'basics' ? (
                    <div className={grid}>
                        <Field label="Event name">{record.eventName}</Field>
                        <Field label="Type">{label(record.eventType)}</Field>
                        <Field label="Billing entity">{record.billingEntity}</Field>
                        <Field label="Guests">{record.guestCount}</Field>
                        <Field label="Description" wide>{record.description}</Field>
                    </div>
                ) : (
                    <div className={grid}>
                        <EditField label="Name" wide>
                            <input className="ps-input w-full text-[13px]" value={draft.name}
                                onChange={(e) => setDraft((d: any) => ({ ...d, name: e.target.value }))} />
                        </EditField>
                        <EditField label="Event name" wide>
                            <input className="ps-input w-full text-[13px]" value={draft.eventName}
                                onChange={(e) => setDraft((d: any) => ({ ...d, eventName: e.target.value }))} />
                        </EditField>
                        <EditField label="Type">
                            <select className={selectClass} value={draft.eventType}
                                onChange={(e) => setDraft((d: any) => ({ ...d, eventType: e.target.value }))}>
                                {EVENT_TYPES.map((t) => (
                                    <option key={t} value={t}>{label(t)}</option>
                                ))}
                            </select>
                        </EditField>
                        <EditField label="Billing entity">
                            <input className="ps-input w-full text-[13px]" value={draft.billingEntity}
                                onChange={(e) => setDraft((d: any) => ({ ...d, billingEntity: e.target.value }))} />
                        </EditField>
                        <EditField label="Guests">
                            <input type="number" className="ps-input w-[110px] text-[13px]" value={draft.guestCount}
                                onChange={(e) => setDraft((d: any) => ({ ...d, guestCount: e.target.value }))} />
                        </EditField>
                        <EditField label="Description" wide>
                            <textarea className="ps-input h-[64px] w-full resize-none text-[13px]" value={draft.description}
                                onChange={(e) => setDraft((d: any) => ({ ...d, description: e.target.value }))} />
                        </EditField>
                    </div>
                )}
            </section>

            {/* schedule */}
            <section className="mb-9">
                <SectionHead
                    title={`Schedule${tz ? ` · ${tz}` : ''}`}
                    editing={editing === 'schedule'}
                    saving={saving}
                    onEdit={() =>
                        startEdit('schedule', {
                            deliveryAt: toLocalInput(record.deliveryAt, tz),
                            showReadyAt: toLocalInput(record.showReadyAt, tz),
                            startAt: toLocalInput(record.startAt, tz),
                            endAt: toLocalInput(record.endAt, tz),
                            timezone: tz ?? 'America/New_York',
                        })
                    }
                    onSave={() =>
                        saveEdit({
                            deliveryAt: fromLocalInput(draft.deliveryAt, draft.timezone),
                            showReadyAt: fromLocalInput(draft.showReadyAt, draft.timezone),
                            startAt: fromLocalInput(draft.startAt, draft.timezone),
                            endAt: fromLocalInput(draft.endAt, draft.timezone),
                            timezone: draft.timezone || null,
                        })
                    }
                    onCancel={() => setEditing(null)}
                />
                {editing !== 'schedule' ? (
                    <div className={grid}>
                        <Field label="Delivery">{fmtAt(record.deliveryAt, tz)}</Field>
                        <Field label="Show ready">{fmtAt(record.showReadyAt, tz)}</Field>
                        <Field label="Start">{fmtAt(record.startAt, tz)}</Field>
                        <Field label="End">{fmtAt(record.endAt, tz)}</Field>
                    </div>
                ) : (
                    <div className={grid}>
                        {(['deliveryAt', 'showReadyAt', 'startAt', 'endAt'] as const).map((k, i) => (
                            <EditField key={k} label={['Delivery', 'Show ready', 'Start', 'End'][i]}>
                                <input type="datetime-local" className="ps-input w-full text-[12.5px]" value={draft[k]}
                                    onChange={(e) => setDraft((d: any) => ({ ...d, [k]: e.target.value }))} />
                            </EditField>
                        ))}
                        <EditField label="Timezone" wide>
                            <select className={selectClass} value={draft.timezone}
                                onChange={(e) => setDraft((d: any) => ({ ...d, timezone: e.target.value }))}>
                                {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'Pacific/Honolulu'].map((z) => (
                                    <option key={z} value={z}>{z}</option>
                                ))}
                            </select>
                        </EditField>
                    </div>
                )}
            </section>

            {/* venue */}
            <section className="mb-9">
                <SectionHead
                    title="Venue"
                    editing={editing === 'venue'}
                    saving={saving}
                    onEdit={() =>
                        startEdit('venue', {
                            venueId: record.venueId ?? '',
                            setupLocation: record.setupLocation ?? '',
                            internetProvided: !!record.internetProvided,
                            internetDetails: record.internetDetails ?? '',
                            loadInInstructions: record.loadInInstructions ?? '',
                            parkingInstructions: record.parkingInstructions ?? '',
                        })
                    }
                    onSave={() => {
                        const venue = venueList.find((v) => +v.id === +draft.venueId);
                        return saveEdit({
                            venueId: draft.venueId === '' ? null : +draft.venueId,
                            venueName: venue?.name ?? null,
                            setupLocation: draft.setupLocation || null,
                            internetProvided: draft.internetProvided,
                            internetDetails: draft.internetDetails || null,
                            loadInInstructions: draft.loadInInstructions || null,
                            parkingInstructions: draft.parkingInstructions || null,
                        });
                    }}
                    onCancel={() => setEditing(null)}
                />
                {editing !== 'venue' ? (
                    <div className={grid}>
                        <Field label="Location" wide>
                            {record.venueName ? (
                                <>
                                    {record.venueName}{' '}
                                    <span className="font-normal text-faint">
                                        {record.venueAddress ? `· ${record.venueAddress}` : ''}
                                    </span>
                                </>
                            ) : null}
                        </Field>
                        <Field label="Setup location">{record.setupLocation}</Field>
                        <Field label="Internet">
                            {record.internetProvided
                                ? (record.internetDetails ?? 'Provided')
                                : 'Not provided'}
                        </Field>
                        <Field label="Load-in" wide>{record.loadInInstructions}</Field>
                        <Field label="Parking" wide>{record.parkingInstructions}</Field>
                    </div>
                ) : (
                    <div className={grid}>
                        <EditField label="Venue" wide>
                            <select className={selectClass + ' w-full'} value={draft.venueId}
                                onChange={(e) => {
                                    const venue = venueList.find((v) => +v.id === +e.target.value);
                                    setDraft((d: any) => ({
                                        ...d,
                                        venueId: e.target.value,
                                        /* venue defaults prefill empty fields */
                                        loadInInstructions: d.loadInInstructions || venue?.defaultLoadInInstructions || '',
                                        parkingInstructions: d.parkingInstructions || venue?.defaultParkingInstructions || '',
                                        internetProvided: d.internetProvided || !!venue?.defaultInternetProvided,
                                        internetDetails: d.internetDetails || venue?.defaultInternetDetails || '',
                                    }));
                                }}>
                                <option value="">No venue</option>
                                {venueList.map((v) => (
                                    <option key={v.id} value={v.id}>{v.name}</option>
                                ))}
                            </select>
                        </EditField>
                        <EditField label="Setup location" wide>
                            <input className="ps-input w-full text-[13px]" value={draft.setupLocation}
                                onChange={(e) => setDraft((d: any) => ({ ...d, setupLocation: e.target.value }))} />
                        </EditField>
                        <EditField label="Internet">
                            <select className={selectClass} value={draft.internetProvided ? 'yes' : 'no'}
                                onChange={(e) => setDraft((d: any) => ({ ...d, internetProvided: e.target.value === 'yes' }))}>
                                <option value="no">Not provided</option>
                                <option value="yes">Provided</option>
                            </select>
                        </EditField>
                        <EditField label="Internet details">
                            <input className="ps-input w-full text-[13px]" value={draft.internetDetails}
                                onChange={(e) => setDraft((d: any) => ({ ...d, internetDetails: e.target.value }))} />
                        </EditField>
                        <EditField label="Load-in" wide>
                            <textarea className="ps-input h-[52px] w-full resize-none text-[13px]" value={draft.loadInInstructions}
                                onChange={(e) => setDraft((d: any) => ({ ...d, loadInInstructions: e.target.value }))} />
                        </EditField>
                        <EditField label="Parking" wide>
                            <textarea className="ps-input h-[52px] w-full resize-none text-[13px]" value={draft.parkingInstructions}
                                onChange={(e) => setDraft((d: any) => ({ ...d, parkingInstructions: e.target.value }))} />
                        </EditField>
                    </div>
                )}
            </section>

            {/* crew */}
            <section className="mb-9">
                <SectionHead
                    title="Crew"
                    editing={editing === 'crew'}
                    saving={saving}
                    onEdit={() =>
                        startEdit('crew', {
                            producerId: record.producerId ?? '',
                            technicalLeadId: record.technicalLeadId ?? '',
                            logisticsCoordinatorId: record.logisticsCoordinatorId ?? '',
                        })
                    }
                    onSave={() =>
                        saveEdit({
                            producerId: draft.producerId === '' ? null : +draft.producerId,
                            technicalLeadId: draft.technicalLeadId === '' ? null : +draft.technicalLeadId,
                            logisticsCoordinatorId: draft.logisticsCoordinatorId === '' ? null : +draft.logisticsCoordinatorId,
                        })
                    }
                    onCancel={() => setEditing(null)}
                />
                {editing !== 'crew' ? (
                    <div className="mt-2.5 flex flex-wrap gap-2.5">
                        {[
                            { l: 'Producer', name: record.producerFirstName ? fullName({ firstName: record.producerFirstName, lastName: record.producerLastName }) : null },
                            { l: 'Technical lead', name: record.technicalLeadFirstName ? fullName({ firstName: record.technicalLeadFirstName, lastName: record.technicalLeadLastName }) : null },
                            { l: 'Logistics', name: record.logisticsCoordinatorFirstName ? fullName({ firstName: record.logisticsCoordinatorFirstName, lastName: record.logisticsCoordinatorLastName }) : null },
                        ].map((kp) => (
                            <span key={kp.l}
                                className={'flex items-center rounded-full border px-3.5 py-2 ' + (kp.name ? 'border-border' : 'border-dashed border-border')}>
                                <span>
                                    <span className="ps-label block text-[8.5px]">{kp.l}</span>
                                    <span className={'mt-px block text-[12.5px] ' + (kp.name ? 'font-semibold' : 'text-faint')}>
                                        {kp.name ?? 'Unassigned'}
                                    </span>
                                </span>
                            </span>
                        ))}
                    </div>
                ) : (
                    <div className={grid}>
                        {([['producerId', 'Producer'], ['technicalLeadId', 'Technical lead'], ['logisticsCoordinatorId', 'Logistics']] as const).map(([key, l]) => (
                            <EditField key={key} label={l}>
                                <select className={selectClass + ' w-full'} value={draft[key]}
                                    onChange={(e) => setDraft((d: any) => ({ ...d, [key]: e.target.value }))}>
                                    <option value="">Unassigned</option>
                                    {staffList.map((s) => (
                                        <option key={s.id} value={s.id}>{fullName(s)}</option>
                                    ))}
                                </select>
                            </EditField>
                        ))}
                    </div>
                )}

                <div className="mt-4">
                    {crew.map((a: any) => (
                        <div key={a.id}
                            className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px] first:border-t-0">
                            <span className="w-[170px] flex-none truncate font-semibold">{fullName(a)}</span>
                            <span className="w-[100px] flex-none text-muted-foreground">{a.role}</span>
                            <span className="w-[100px] flex-none tabular-nums text-muted-foreground">
                                {a.rate ? (a.rateType === 'flat' ? `$${+a.rate} flat` : `$${+a.rate}/hr`) : '—'}
                            </span>
                            <span className="min-w-0 flex-1" />
                            <select className={selectClass} value={a.status}
                                onChange={(e) => mutate(() => api.setStaffAssignmentStatus(+a.id, e.target.value))}>
                                {ASSIGNMENT_STATUSES.map((s) => (
                                    <option key={s} value={s}>{label(s)}</option>
                                ))}
                            </select>
                            <button type="button"
                                onClick={() => mutate(() => api.removeStaffAssignment(+a.id))}
                                className="cursor-pointer border-0 bg-transparent p-0 text-faint hover:text-attention">
                                ✕
                            </button>
                        </div>
                    ))}
                    {/* add crew row */}
                    <div className="flex flex-wrap items-center gap-2.5 border-t border-dashed border-border py-2.5">
                        <select className={selectClass + ' w-[180px]'} value={crewAdd.staffId}
                            onChange={(e) => setCrewAdd((c) => ({ ...c, staffId: e.target.value }))}>
                            <option value="">Add crew…</option>
                            {staffList
                                .filter((s) => !crew.some((a: any) => +a.staffId === +s.id))
                                .map((s) => (
                                    <option key={s.id} value={s.id}>{fullName(s)}</option>
                                ))}
                        </select>
                        {crewAdd.staffId && (
                            <>
                                <select className={selectClass} value={crewAdd.role}
                                    onChange={(e) => setCrewAdd((c) => ({ ...c, role: e.target.value }))}>
                                    {STAFF_ROLES.map((r) => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                                <input className="ps-input w-[90px] text-[12.5px]" placeholder="Rate"
                                    value={crewAdd.rate} type="number"
                                    onChange={(e) => setCrewAdd((c) => ({ ...c, rate: e.target.value }))} />
                                <select className={selectClass} value={crewAdd.rateType}
                                    onChange={(e) => setCrewAdd((c) => ({ ...c, rateType: e.target.value }))}>
                                    <option value="hourly">/hr</option>
                                    <option value="flat">flat</option>
                                </select>
                                <button type="button"
                                    onClick={() =>
                                        mutate(() =>
                                            api.addStaffAssignment(id, {
                                                staffId: +crewAdd.staffId,
                                                role: crewAdd.role,
                                                rate: crewAdd.rate === '' ? undefined : +crewAdd.rate,
                                                rateType: crewAdd.rateType,
                                            }),
                                        ).then(() => setCrewAdd((c) => ({ ...c, staffId: '', rate: '' })))
                                    }
                                    className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1.5 font-[inherit] text-[12px] font-semibold text-white">
                                    Add
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* booth */}
            <section className="mb-9">
                <SectionHead
                    title="Booth"
                    editing={editing === 'booth'}
                    saving={saving}
                    onEdit={() =>
                        startEdit('booth', {
                            boothType: record.boothType ?? '',
                            software: record.software ?? '',
                            backdrop: record.backdrop ?? '',
                            printing: record.printing ?? '',
                            slideshow: record.slideshow ?? '',
                            attire: record.attire ?? '',
                            equipment: record.equipment ?? '',
                        })
                    }
                    onSave={() =>
                        saveEdit({
                            boothType: draft.boothType || null,
                            software: draft.software || null,
                            backdrop: draft.backdrop || null,
                            printing: draft.printing || null,
                            slideshow: draft.slideshow || null,
                            attire: draft.attire || null,
                            equipment: draft.equipment || null,
                        })
                    }
                    onCancel={() => setEditing(null)}
                />
                {editing !== 'booth' ? (
                    <div className={grid}>
                        <Field label="Booth type">{record.boothType ? label(record.boothType) : null}</Field>
                        <Field label="Software">{record.software ? label(record.software) : null}</Field>
                        <Field label="Backdrop">{record.backdrop ? label(record.backdrop) : null}</Field>
                        <Field label="Printing">{record.printing}</Field>
                        <Field label="Slideshow">{record.slideshow}</Field>
                        <Field label="Attire">{record.attire}</Field>
                        <Field label="Equipment notes" wide>{record.equipment}</Field>
                    </div>
                ) : (
                    <div className={grid}>
                        <EditField label="Booth type">
                            <select className={selectClass} value={draft.boothType}
                                onChange={(e) => setDraft((d: any) => ({ ...d, boothType: e.target.value }))}>
                                <option value="">—</option>
                                {BOOTH_TYPES.map((t) => (
                                    <option key={t} value={t}>{label(t)}</option>
                                ))}
                            </select>
                        </EditField>
                        <EditField label="Software">
                            <select className={selectClass} value={draft.software}
                                onChange={(e) => setDraft((d: any) => ({ ...d, software: e.target.value }))}>
                                <option value="">—</option>
                                {SOFTWARE.map((t) => (
                                    <option key={t} value={t}>{label(t)}</option>
                                ))}
                            </select>
                        </EditField>
                        <EditField label="Backdrop">
                            <select className={selectClass} value={draft.backdrop}
                                onChange={(e) => setDraft((d: any) => ({ ...d, backdrop: e.target.value }))}>
                                <option value="">—</option>
                                {BACKDROPS.map((t) => (
                                    <option key={t} value={t}>{label(t)}</option>
                                ))}
                            </select>
                        </EditField>
                        <EditField label="Printing">
                            <input className="ps-input w-full text-[13px]" value={draft.printing}
                                onChange={(e) => setDraft((d: any) => ({ ...d, printing: e.target.value }))} />
                        </EditField>
                        <EditField label="Slideshow">
                            <input className="ps-input w-full text-[13px]" value={draft.slideshow}
                                onChange={(e) => setDraft((d: any) => ({ ...d, slideshow: e.target.value }))} />
                        </EditField>
                        <EditField label="Attire">
                            <input className="ps-input w-full text-[13px]" value={draft.attire}
                                onChange={(e) => setDraft((d: any) => ({ ...d, attire: e.target.value }))} />
                        </EditField>
                        <EditField label="Equipment notes" wide>
                            <textarea className="ps-input h-[52px] w-full resize-none text-[13px]" value={draft.equipment}
                                onChange={(e) => setDraft((d: any) => ({ ...d, equipment: e.target.value }))} />
                        </EditField>
                    </div>
                )}
            </section>

            {/* gear */}
            <section className="mb-9">
                <div className="ps-label">Gear</div>
                <div className="mt-2.5">
                    {gear.map((g: any) => (
                        <div key={g.id}
                            className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px] first:border-t-0">
                            <span className="w-[220px] flex-none truncate font-semibold">{g.assetName}</span>
                            <span className="w-[140px] flex-none text-muted-foreground">
                                {[g.brand, g.model].filter(Boolean).join(' ')}
                            </span>
                            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-faint">{g.serialNumber}</span>
                            <span className="ps-label flex-none normal-case">{label(g.status)}</span>
                            <button type="button"
                                onClick={() => mutate(() => api.removeGearAssignment(+g.id))}
                                className="cursor-pointer border-0 bg-transparent p-0 text-faint hover:text-attention">
                                ✕
                            </button>
                        </div>
                    ))}
                    <div className="flex items-center gap-2.5 border-t border-dashed border-border py-2.5">
                        <select className={selectClass + ' w-[240px]'} value={gearAdd.gearAssetId}
                            onChange={(e) => setGearAdd({ gearAssetId: e.target.value })}>
                            <option value="">Reserve gear…</option>
                            {gearList
                                .filter((asset) => !gear.some((g: any) => +g.gearAssetId === +asset.id))
                                .map((asset) => (
                                    <option key={asset.id} value={asset.id}>
                                        {asset.name} ({asset.serialNumber ?? asset.category})
                                    </option>
                                ))}
                        </select>
                        {gearAdd.gearAssetId && (
                            <button type="button"
                                onClick={() =>
                                    mutate(() =>
                                        api.addGearAssignment(id, {
                                            gearAssetId: +gearAdd.gearAssetId,
                                        }),
                                    ).then(() => setGearAdd({ gearAssetId: '' }))
                                }
                                className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1.5 font-[inherit] text-[12px] font-semibold text-white">
                                Reserve
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* contacts */}
            <section className="mb-9">
                <div className="ps-label">Contacts</div>
                <div className="mt-2.5">
                    {contacts.map((c: any) => (
                        <div key={c.activationContactId}
                            className="flex items-center gap-3 border-t border-line py-[9px] text-[12.5px] first:border-t-0">
                            <span className="w-[170px] flex-none truncate font-semibold">{c.name}</span>
                            <span className="w-[90px] flex-none text-muted-foreground">{label(c.role)}</span>
                            <span className="min-w-0 flex-1 truncate text-faint">
                                {[c.email, c.phone, c.companyName].filter(Boolean).join(' · ')}
                            </span>
                            {c.attioId && (
                                <a className="flex-none text-[12px] font-semibold text-brand-ink"
                                    href={`https://app.attio.com/smilebooth/people/record/${c.attioId}/overview`}
                                    target="_blank" rel="noreferrer">
                                    Attio ↗
                                </a>
                            )}
                            <button type="button"
                                onClick={() => mutate(() => api.removeActivationContact(+c.activationContactId))}
                                className="cursor-pointer border-0 bg-transparent p-0 text-faint hover:text-attention">
                                ✕
                            </button>
                        </div>
                    ))}
                    <div className="flex items-center gap-2.5 border-t border-dashed border-border py-2.5">
                        <select className={selectClass + ' w-[220px]'} value={contactAdd.contactId}
                            onChange={(e) => setContactAdd((c) => ({ ...c, contactId: e.target.value }))}>
                            <option value="">Add contact…</option>
                            {contactList.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}{c.companyName ? ` — ${c.companyName}` : ''}
                                </option>
                            ))}
                        </select>
                        {contactAdd.contactId && (
                            <>
                                <select className={selectClass} value={contactAdd.role}
                                    onChange={(e) => setContactAdd((c) => ({ ...c, role: e.target.value }))}>
                                    {CONTACT_ROLES.map((r) => (
                                        <option key={r} value={r}>{label(r)}</option>
                                    ))}
                                </select>
                                <button type="button"
                                    onClick={() =>
                                        mutate(() =>
                                            api.addActivationContact(id, {
                                                contactId: +contactAdd.contactId,
                                                role: contactAdd.role,
                                            }),
                                        ).then(() => setContactAdd({ contactId: '', role: 'primary' }))
                                    }
                                    className="cursor-pointer rounded-full border-0 bg-brand px-3.5 py-1.5 font-[inherit] text-[12px] font-semibold text-white">
                                    Add
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* design */}
            <section className="mb-9">
                <div className="flex items-center gap-3">
                    <span className="ps-label">Design</span>
                    <button type="button"
                        onClick={() => mutate(() => api.createDesignRequest({ activationId: id }))}
                        className="cursor-pointer border-0 bg-transparent p-0 font-[inherit] text-[11.5px] font-semibold text-faint hover:text-brand-ink">
                        + New request
                    </button>
                </div>
                <div className="mt-2.5">
                    {design.length === 0 && (
                        <div className="text-[12.5px] text-faint">No design requests.</div>
                    )}
                    {design.map((d: any) => (
                        <div key={d.id} className="mb-4">
                            <div className="flex items-center gap-3 text-[12.5px]">
                                <select className={selectClass} value={d.status}
                                    onChange={(e) => mutate(() => api.updateDesignRequest(+d.id, { status: e.target.value }))}>
                                    {DESIGN_STATUSES.map((s) => (
                                        <option key={s} value={s}>{label(s)}</option>
                                    ))}
                                </select>
                                <input type="date" className="ps-input text-[12px]"
                                    value={d.dueDate ? moment(d.dueDate).format('YYYY-MM-DD') : ''}
                                    onChange={(e) => mutate(() => api.updateDesignRequest(+d.id, { dueDate: e.target.value || null }))} />
                                <select className={selectClass}
                                    value={d.assignedDesignerStaffId ?? ''}
                                    onChange={(e) => mutate(() => api.updateDesignRequest(+d.id, { assignedDesignerStaffId: e.target.value === '' ? null : +e.target.value }))}>
                                    <option value="">Unassigned</option>
                                    {staffList.map((s) => (
                                        <option key={s.id} value={s.id}>{fullName(s)}</option>
                                    ))}
                                </select>
                            </div>
                            {(d.deliverables ?? []).map((del: any) => (
                                <div key={del.id}
                                    className="flex items-center gap-3 border-t border-line py-[7px] text-[12.5px] first:border-t-0">
                                    <span className="min-w-0 flex-1 truncate">{del.label}</span>
                                    <span className="ps-label flex-none normal-case text-faint">{label(del.type)}</span>
                                    <select className={selectClass} value={del.status}
                                        onChange={(e) => mutate(() => api.updateDeliverable(+del.id, { status: e.target.value }))}>
                                        {DELIVERABLE_STATUSES.map((s) => (
                                            <option key={s} value={s}>{label(s)}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                            <div className="flex items-center gap-2.5 border-t border-dashed border-border py-2">
                                <input className="ps-input w-[240px] text-[12.5px]" placeholder="Add deliverable…"
                                    value={deliverableAdd[d.id] ?? ''}
                                    onChange={(e) => setDeliverableAdd((m) => ({ ...m, [d.id]: e.target.value }))}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && (deliverableAdd[d.id] ?? '').trim()) {
                                            mutate(() =>
                                                api.createDeliverable(+d.id, {
                                                    label: deliverableAdd[d.id].trim(),
                                                }),
                                            ).then(() => setDeliverableAdd((m) => ({ ...m, [d.id]: '' })));
                                        }
                                    }} />
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* comms + links */}
            <section className="mb-9">
                <SectionHead
                    title="Comms & links"
                    editing={editing === 'comms'}
                    saving={saving}
                    onEdit={() =>
                        startEdit('comms', {
                            emailSubject: record.emailSubject ?? '',
                            emailBody: record.emailBody ?? '',
                            textMessageBody: record.textMessageBody ?? '',
                            dropboxFolderUrl: record.dropboxFolderUrl ?? '',
                            notes: record.notes ?? '',
                        })
                    }
                    onSave={() =>
                        saveEdit({
                            emailSubject: draft.emailSubject || null,
                            emailBody: draft.emailBody || null,
                            textMessageBody: draft.textMessageBody || null,
                            dropboxFolderUrl: draft.dropboxFolderUrl || null,
                            notes: draft.notes || null,
                        })
                    }
                    onCancel={() => setEditing(null)}
                />
                {editing !== 'comms' ? (
                    <div className={grid}>
                        <Field label="Email subject" wide>{record.emailSubject}</Field>
                        <Field label="Text message" wide>{record.textMessageBody}</Field>
                        <Field label="Dropbox">
                            {record.dropboxFolderUrl ? (
                                <a className="text-brand-ink" href={record.dropboxFolderUrl} target="_blank" rel="noreferrer">
                                    Folder ↗
                                </a>
                            ) : null}
                        </Field>
                        <Field label="Notes" wide>{record.notes}</Field>
                    </div>
                ) : (
                    <div className={grid}>
                        <EditField label="Email subject" wide>
                            <input className="ps-input w-full text-[13px]" value={draft.emailSubject}
                                onChange={(e) => setDraft((d: any) => ({ ...d, emailSubject: e.target.value }))} />
                        </EditField>
                        <EditField label="Email body" wide>
                            <textarea className="ps-input h-[64px] w-full resize-none text-[13px]" value={draft.emailBody}
                                onChange={(e) => setDraft((d: any) => ({ ...d, emailBody: e.target.value }))} />
                        </EditField>
                        <EditField label="Text message" wide>
                            <textarea className="ps-input h-[52px] w-full resize-none text-[13px]" value={draft.textMessageBody}
                                onChange={(e) => setDraft((d: any) => ({ ...d, textMessageBody: e.target.value }))} />
                        </EditField>
                        <EditField label="Dropbox URL" wide>
                            <input className="ps-input w-full text-[13px]" value={draft.dropboxFolderUrl}
                                onChange={(e) => setDraft((d: any) => ({ ...d, dropboxFolderUrl: e.target.value }))} />
                        </EditField>
                        <EditField label="Notes" wide>
                            <textarea className="ps-input h-[64px] w-full resize-none text-[13px]" value={draft.notes}
                                onChange={(e) => setDraft((d: any) => ({ ...d, notes: e.target.value }))} />
                        </EditField>
                    </div>
                )}
            </section>

            {/* timeline */}
            <section>
                <div className="ps-label">Timeline</div>
                <div className="mt-3 border-l-2 border-line pl-4">
                    {activity.length === 0 && (
                        <div className="text-[12.5px] text-faint">No activity yet.</div>
                    )}
                    {activity.map((e: any) => (
                        <div key={e.id} className="relative pb-4">
                            <span className="absolute -left-[21.5px] top-1 h-[9px] w-[9px] rounded-full border-2 border-background bg-off" />
                            <div className="text-[12.5px]">
                                <b className="font-semibold">{label(e.action)}</b>
                                {(e.actorName || e.actorUsername) ? ` · ${e.actorName ?? e.actorUsername}` : ''}
                            </div>
                            <div className="mt-0.5 text-[11px] text-faint">
                                {moment(e.occurredAt).format('MMM D, h:mm A')}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
